import type { Poll, Score, Team, Timer } from './types';

/**
 * Was angesagt wird.
 *
 * Reine Rechnung: Aus zwei aufeinanderfolgenden Ständen wird eine Liste von Sätzen. Gesprochen
 * wird woanders – dadurch lässt sich hier ohne Browser prüfen, **was** gesagt wird und vor allem,
 * was **nicht**.
 *
 * Drei Grundsätze, die den Zuschnitt erklären:
 *
 * 1. **Nur Veränderungen.** Angesagt wird, was neu ist, nie der Zustand. Sonst spricht es bei
 *    jedem eintreffenden Stand von vorn – und Stände treffen bei jedem geschalteten Feld ein.
 * 2. **Nichts, was ohnehin dasteht.** Punktestände werden nicht vorgelesen; die stehen im Bild.
 *    Angesagt wird, was man verpasst, wenn man gerade woanders hinsieht.
 * 3. **Beim ersten Stand wird geschwiegen.** Wer mitten in der Runde dazukommt oder neu lädt,
 *    soll nicht die Ereignisse der letzten Stunde nachgetragen bekommen.
 */

export interface Stand {
  timer: Timer;
  scores: Score[];
  teams: Team[];
  poll: Poll | null;
}

export interface Ansage {
  /** Eindeutig je Ereignis, damit dasselbe nicht zweimal gesagt wird. */
  schlüssel: string;
  text: string;
  /** Höher bedeutet wichtiger. Steht viel an, wird nur das Wichtigste gesagt. */
  gewicht: number;
}

/** Höchstens so viele Ansagen auf einmal – darüber redet es schneller, als jemand zuhört. */
const MAX_AUF_EINMAL = 3;

export function ansagenFür(vorher: Stand | null, jetzt: Stand): Ansage[] {
  // Beim ersten Stand gibt es nichts zu melden – es ist nichts geschehen, man kommt nur dazu.
  if (!vorher) return [];

  const ansagen: Ansage[] = [];
  const teamName = (id: string | null) =>
    jetzt.teams.find((t) => t.id === id)?.name ?? 'Ein Team';

  // --- Runde ---------------------------------------------------------------

  if (vorher.timer.phase !== 'RUNNING' && jetzt.timer.phase === 'RUNNING') {
    ansagen.push({
      schlüssel: `timer-start-${jetzt.timer.durationSeconds}`,
      text: vorher.timer.phase === 'PAUSED' ? 'Weiter geht es.' : 'Die Runde läuft.',
      gewicht: 80,
    });
  }

  if (vorher.timer.phase === 'RUNNING' && jetzt.timer.phase === 'PAUSED') {
    ansagen.push({ schlüssel: 'timer-pause', text: 'Die Runde ist pausiert.', gewicht: 70 });
  }

  if (vorher.timer.phase !== 'ENDED' && jetzt.timer.phase === 'ENDED' && vorher.timer.remainingSeconds > 1) {
    const führend = bestes(jetzt);
    ansagen.push({
      schlüssel: 'runde-beendet',
      text: führend
        ? `Die Runde ist beendet. Vorn liegt ${führend}.`
        : 'Die Runde ist beendet.',
      gewicht: 100,
    });
  }

  // --- Bingos --------------------------------------------------------------

  for (const score of jetzt.scores) {
    const alt = vorher.scores.find((s) => s.teamId === score.teamId);
    const vorBingos = alt?.bingos ?? 0;

    if (score.bingos > vorBingos) {
      ansagen.push({
        schlüssel: `bingo-${score.teamId}-${score.bingos}`,
        text: `Bingo für ${teamName(score.teamId)}!`,
        gewicht: 90,
      });
    }

    if (score.fullBoard && !alt?.fullBoard) {
      ansagen.push({
        schlüssel: `vollbrett-${score.teamId}`,
        text: `${teamName(score.teamId)} hat das ganze Brett voll!`,
        gewicht: 100,
      });
    }
  }

  // --- Abstimmungen --------------------------------------------------------

  const poll = jetzt.poll;
  const vorPoll = vorher.poll;

  if (poll?.offen && (!vorPoll?.offen || vorPoll.runId !== poll.runId)) {
    ansagen.push({ schlüssel: `poll-start-${poll.runId}`, text: 'Es wurde eine neue Umfrage gestartet.', gewicht: 95 });
  }

  if (poll && !poll.offen && poll.closedAt && vorPoll?.runId === poll.runId && !vorPoll.closedAt) {
    ansagen.push(...ergebnisAnsagen(poll, teamName));
  }

  return ansagen
    .sort((a, b) => b.gewicht - a.gewicht)
    .slice(0, MAX_AUF_EINMAL);
}

/** Was nach dem Schließen einer Abstimmung zu sagen ist. */
function ergebnisAnsagen(poll: Poll, teamName: (id: string | null) => string): Ansage[] {
  if (poll.mode === 'PER_CHANNEL' && poll.jeKanal) {
    return poll.jeKanal
      .filter((e) => e.gewinner)
      .map((e) => ({
        schlüssel: `challenge-${poll.runId}-${e.channelId}`,
        text: e.gewinner!.challenge,
        gewicht: 95,
      }));
  }

  if (poll.ergebnis?.gewinner) {
    return [
      {
        schlüssel: `challenge-${poll.runId}`,
        text: poll.ergebnis.gewinner.challenge,
        gewicht: 95,
      },
    ];
  }

  // Ein Gleichstand wird gesagt, nicht verschwiegen – sonst wartet man auf eine Challenge, die
  // nicht kommt.
  if (poll.ergebnis?.gleichstand) {
    return [
      {
        schlüssel: `challenge-gleich-${poll.runId}`,
        text: `Gleichstand zwischen ${poll.ergebnis.führende.map((a) => a.label).join(' und ')}.`,
        gewicht: 90,
      },
    ];
  }

  return [];
}

/** Wer vorn liegt – oder nichts, wenn alle gleichauf sind. */
function bestes(stand: Stand): string | null {
  if (stand.scores.length === 0) return null;

  const höchste = Math.max(...stand.scores.map((s) => s.total));
  if (höchste === 0) return null;

  const führende = stand.scores.filter((s) => s.total === höchste);
  if (führende.length !== 1) return null;

  return stand.teams.find((t) => t.id === führende[0]!.teamId)?.name ?? null;
}
